import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-widget-layout.st.css';
import {DoppeActionTypeWidgetHeader} from '../doppe-action-type-widget-header/doppe-action-type-widget-header';
import {IDoppeViewerActionContext, useBymoPageContextProps} from '../../doppe-sdk';
import {AnimatedAutoSizedElement, useEventCallback, useObservable, useObservableValue} from '@wix/devzai-utils-react';
import {
    DoppeActionBackHeaderNew
} from '../../doppe-action-types/doppe-actions-common/doppe-action-back-header-new/doppe-action-back-header-new';
import {assertDefined, evaluateFunction, Nullable, OmitStrict} from '@wix/devzai-utils-common';
import {DoppeViewerModalPanelHeader} from '../doppe-viewer-modal-panel-header/doppe-viewer-modal-panel-header';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    doppeDtoActionShouldShowActionHeader,
    DoppeDtoActionViewType
} from '../../client-server-common/types/doppe-dto-action';
import {DoppeActionLockPreview, DoppeActionLockReason} from '../doppe-action-lock-preview/doppe-action-lock-preview';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {
    doppeViewerActionSetIsRenderingInnerState
} from '../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {DoppeActionLockPreviewMode} from '../../client-server-common/types/doppe-action-lock-preview-mode';
import {doppeHideableValueGetVisibleValue} from '../../client-server-common';
import {
    useAssertWidgetRenderingContext, useIsWidgetInFixedHeight
} from "../doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context";

export type DoppeActionTypeWidgetLayoutHeaderSpec = {
    hasBorderBottom: boolean;
}

export const DoppeActionTypeWidgetLayout = React.memo(React.forwardRef<HTMLDivElement, DoppeActionTypeWidgetLayout.Props>(function DoppeActionTypeWidgetLayout(props, ref) {
    const {
        children,
        actionContext,
        icon,
        isForm = false,
        forceMaxHeight = false,
        noValidate = false,
        className,
        ...htmlAttributes
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const isLockedObservable = clientServices.lockService.getActionLockObservable(actionContext.action);
    const isLocked = useObservable(isLockedObservable, observable => observable?.getValue() ?? false);


    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(actionContext.action.actionTypeId),
        `Action type with id '${actionContext.action.actionTypeId}' isn't registered`);

    const {
        hideHeader,
        renderingInnerStateController,
        headerCustomSettings,
        observableCurrentItemIdToRender,
        isFullScreen,
    } = useAssertWidgetRenderingContext();

    const bymoPageContextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();


    const isActionInEditMode = pageViewerContext.mode === BymoPageViewerMode.LivePreview && bymoPageContextProps.editorEditedActionId === actionContext.action.id;

    const currentItemIdToRender = useObservableValue(observableCurrentItemIdToRender);
    const currentActionListItem = useMemo(() => {
        return currentItemIdToRender ? (actionType.resolveListItems?.(actionContext.action) ?? []).find(item => {
            return item.itemId === currentItemIdToRender;
        }) : undefined
    }, [currentItemIdToRender, actionContext.action.settings]);

    const inInnerNavigation = useObservableValue(renderingInnerStateController);

    const isWidgetInFixedHeight = useIsWidgetInFixedHeight();

    const isStrip = actionContext.getActionViewType() === DoppeDtoActionViewType.InPageFrameless;

    const {
        customLockPreviewDescription,
        customLockPreviewMedia,
        customLockPreviewTitle,
        customLockPreviewButtonText,
        lockPreviewShowActionHeader,
        lockPreviewMode,
        customPreviewContent
    } = actionContext.action.lockSettings;

    const isDefaultLockPreviewMode = !customPreviewContent;
    const isLockCTAPreviewMode = lockPreviewMode === DoppeActionLockPreviewMode.LockCTA;

    const isActualLocked = isLocked && !isActionInEditMode && !isLockCTAPreviewMode;

    const unlockCallback = useEventCallback(async () => {
        return actionContext.startUnlocking(() => {
            isLockedObservable?.setValue(false);
            actionContext.toggleActionView(true);
        }, false)
    })


    const headerInfo = evaluateFunction(() => {
        if (hideHeader) {
            return null;
        }

        if (isActualLocked && !lockPreviewShowActionHeader) {
            return null;
        }

        if (headerCustomSettings) {
            return {
                headerComponent: (
                    <DoppeViewerModalPanelHeader
                        className={style(classes.header, {})}
                        backButtonCallback={headerCustomSettings.backButtonCallback ? () => {
                            headerCustomSettings.backButtonCallback?.();
                        } : undefined}
                        dismissButtonCallback={headerCustomSettings.dismissButtonCallback}
                        title={currentActionListItem?.title ?? headerCustomSettings.title}
                        subtitle={!currentActionListItem?.title ? headerCustomSettings.subtitle : undefined}
                        subtitleLines={headerCustomSettings.subtitleLines}
                    />
                ),
                headerSpec: {
                    hasBorderBottom: true
                }
            }
        } else if (inInnerNavigation) {

            const hasBorderBottom = !isStrip;

            return {
                headerComponent: (
                    <DoppeActionBackHeaderNew
                        onBackClick={() => {
                            doppeViewerActionSetIsRenderingInnerState(actionContext, false);
                            observableCurrentItemIdToRender?.setValue(null);
                        }}
                        layout={actionContext.action.settings.layout}
                        title={currentActionListItem?.title ?? actionContext.action.name}
                        hasBorderBottom={hasBorderBottom}
                    />
                ),
                headerSpec: {
                    hasBorderBottom: hasBorderBottom
                }
            };
        } else {

            if (!doppeDtoActionShouldShowActionHeader(actionContext.action)) {
                return null;
            }

            const hasBorderBottom = !isStrip;

            return {
                headerComponent: (
                    <DoppeActionTypeWidgetHeader
                        className={style(classes.header, {})}
                        icon={icon}
                        actionContext={actionContext}
                        hasBorderBottom={hasBorderBottom}
                    />
                ),
                headerSpec: {
                    hasBorderBottom: hasBorderBottom
                }
            };
        }
    }) satisfies Nullable<{ headerComponent: React.ReactElement; headerSpec: DoppeActionTypeWidgetLayoutHeaderSpec }>;

    return React.createElement(isForm ? 'form' : 'div', {
        ...htmlAttributes,
        ...(isForm ? {noValidate: noValidate} : {}),
        ref: ref,
        className: style(
            classes.root,
            {
                isFixedHeight: isWidgetInFixedHeight,
                forceMaxHeight: forceMaxHeight,
                isFullScreen: !!isFullScreen,
            },
            className
        ),
        children: (
            <DoppeActionTypeWidgetLayoutContext.Provider
                value={{
                    forceMaxHeight: forceMaxHeight,
                    headerSpec: headerInfo?.headerSpec ?? null,
                    isStrip: isStrip
                }}
            >
                {headerInfo?.headerComponent ?? null}
                <AnimatedAutoSizedElement
                    className={style(classes.animatedElement, {}, FlexLayout.column.default, FlexLayout.fillRemainingVerticalSpace)}
                    durationMilliseconds={!isWidgetInFixedHeight ? 200 : 0}
                >
                    <div
                        className={style(classes.content, {})}
                    >
                        {isActualLocked ?
                            <DoppeActionLockPreview
                                useDefaultContent={isDefaultLockPreviewMode}
                                heading={customLockPreviewTitle}
                                description={customLockPreviewDescription}
                                media={customLockPreviewMedia}
                                buttonText={doppeHideableValueGetVisibleValue(customLockPreviewButtonText, '')}
                                unlockCallback={unlockCallback}
                                reason={DoppeActionLockReason.Lock}
                            />
                            :
                            children
                        }
                    </div>
                </AnimatedAutoSizedElement>
            </DoppeActionTypeWidgetLayoutContext.Provider>
        )
    })
}));

export namespace DoppeActionTypeWidgetLayout {

    export interface Props extends React.HTMLAttributes<any>, OmitStrict<DoppeActionTypeWidgetHeader.Props, 'hasBorderBottom'> {
        isFixedHeight?: boolean;
        actionContext: IDoppeViewerActionContext<any>;
        isForm?: boolean;
        noValidate?: boolean;
        children: React.ReactElement;
        forceMaxHeight?: boolean;
    }
}

export type DoppeActionTypeWidgetLayoutContextData = {
    forceMaxHeight: boolean;
    headerSpec: DoppeActionTypeWidgetLayoutHeaderSpec | null;
    isStrip: boolean;
}

export const DoppeActionTypeWidgetLayoutContext = React.createContext<DoppeActionTypeWidgetLayoutContextData | null>(null);
